/**
 * Das Reitergedaechtnis (Zusatzauftrag zu Aufgabe 5, Entscheidung des Auftraggebers vom
 * 2026-08-25): seit die Filter in der Adresse leben, verliert ein Reiterwechsel sie - montierte
 * Seitenkomponenten und ihr `useState` ueberlebten das frueher, `useAnsicht` liest dagegen jedes Mal
 * neu aus der URL. Gewuenscht ist, dass ein Reiter auf die zuletzt DORT besuchte Adresse
 * zurueckfuehrt (`/board?q=usd&archiv=all` -> "Uebersicht" -> "Board" -> wieder `q=usd&archiv=all`).
 *
 * DREI HARTE BEDINGUNGEN, in diesem Modul erzwungen:
 *
 * 1. NUR fuer das REITERZIEL gelesen (`zielFuerReiter`, Aufrufer: `Navigation.tsx`), NIE beim
 *    Zeichnen einer Ansicht - dieses Modul hat keinen Aufrufer in `adresse.ts`/`useAnsicht.ts` oder
 *    irgendeiner Seite. Was auf dem Bildschirm steht, kommt ausschliesslich aus der Adresse.
 * 2. DIE ADRESSE GEWINNT IMMER: Neuladen, Vor/Zurueck, ein Tieflink - das Gedaechtnis ist ein
 *    Vorschlag fuer EIN Ziel (den naechsten Reiterklick), keine zweite Zustandsquelle, die in eine
 *    laufende Navigation hineinredet.
 * 3. KEIN localStorage/sessionStorage - ein simpler Modulspeicher, der bei jedem Neuladen der Seite
 *    (einem frischen Modulaufruf) leer beginnt. So hat der Auftraggeber es bestellt.
 *
 * ================= K-2 der Pruefrunde 1 (Fehler in Bedingung 1, nicht in der Umsetzung) ===========
 *
 * Bedingung 1 allein reicht nicht: `merke()` laeuft in `Navigation.tsx`s `useEffect`, also NACH dem
 * Zeichnen. `zielFuerReiter()` wird dagegen BEIM Zeichnen gelesen (das `href` jedes Reiters entsteht
 * im selben Durchlauf). Der Reiter der GERADE AKTIVEN Seite zeigte damit auf die Ansicht VOM VORIGEN
 * Durchlauf, nicht auf die eben gezeichnete - gemessen: Filter loeschen, Adresse korrekt, aber der
 * aktive Reiter traegt noch den alten `href`; ein Klick DARAUF brachte den geloeschten Filter zurueck.
 *
 * Schaerfere Fassung, hier erzwungen: `zielFuerReiter` bekommt die AKTUELLE Ansicht als zweiten
 * Parameter und liefert sie UNVERAENDERT zurueck, wenn nach der angefragten Seite gefragt wird, auf
 * der man sich GERADE befindet - das Gedaechtnis wird fuer den aktiven Reiter gar nicht erst
 * konsultiert. Es bleibt ausschliesslich fuer die Reiter zustaendig, auf denen man gerade NICHT ist.
 */
import { frischeVorgabe, type Ansicht, type Seitenkennung } from "./adresse.ts";

const gedaechtnis = new Map<Seitenkennung, Ansicht>();

/**
 * Schreibt die aktuelle Ansicht unter ihrer Seite ins Gedaechtnis. Aufrufer: `Navigation.tsx`, bei
 * jeder Ansichtsaenderung (nicht nur bei einem Reiterklick) - so traegt das Gedaechtnis auch einen
 * Filter nach, den der Nutzer waehrend des Aufenthalts auf der Seite gesetzt hat.
 */
export function merke(ansicht: Ansicht): void {
  gedaechtnis.set(ansicht.seite, ansicht);
}

/**
 * Das Ziel fuer einen Reiter.
 *
 * Ist `seite` die Seite der `aktuelleAnsicht` (der Reiter, auf dem man GERADE steht), liefert diese
 * Funktion `aktuelleAnsicht` unveraendert zurueck - das Gedaechtnis wird fuer den aktiven Reiter
 * nicht gelesen (K-2 der Pruefrunde 1, siehe Kopfkommentar). Fuer jeden ANDEREN Reiter gilt weiter
 * die zuletzt dort besuchte Ansicht, sonst die Vorgabe (kein Aufenthalt seit dem letzten Neuladen).
 *
 * `frischeVorgabe()` statt `{ ...VORGABE_ANSICHT, seite }` (Befund "Gering" derselben Pruefrunde):
 * `VORGABE_ANSICHT` ist eingefroren, ein flacher Spread daraus teilt ihre UNTERobjekte
 * (`board`/`uebersicht`/...) mit allen anderen Lesern - derselbe Fehler, den `adresse.ts` fuer sich
 * selbst bereits ausschliesst (dortiger Kopfkommentar, Fixrunde 1, Befund 2). `frischeVorgabe()`
 * liefert bei jedem Aufruf neue, unabhaengige Unterobjekte.
 */
export function zielFuerReiter(seite: Seitenkennung, aktuelleAnsicht: Ansicht): Ansicht {
  if (seite === aktuelleAnsicht.seite) return aktuelleAnsicht;
  return gedaechtnis.get(seite) ?? { ...frischeVorgabe(), seite };
}
